/**
 * Resolve pricing for a product that may store its price at the top level
 * or inside its first variant.
 *
 * Variant-based products have no top-level price/offerPrice — the Product
 * model's pre-save hook clears them whenever `variants` is non-empty — so any
 * screen that reads `product.price` / `product.offerPrice` directly shows a
 * blank/undefined price for those products. Use this helper instead.
 *
 * Package charge follows the same rule but falls back: a variant's own
 * packageCharge (if set, including 0) takes precedence over the product's.
 *
 * @param {Object} product - Product object (may include a `variants` array)
 * @returns {{ price: number, offerPrice: number, effectivePrice: number, packageCharge: number }}
 */
export const resolveProductPricing = (product) => {
  const hasVariants =
    Array.isArray(product?.variants) && product.variants.length > 0;
  const source = hasVariants ? product.variants[0] : product;
  const price = Number(source?.price) || 0;
  const offerPrice = Number(source?.offerPrice) || 0;
  const variantPackageCharge = hasVariants ? source?.packageCharge : null;
  const packageCharge =
    variantPackageCharge !== null && variantPackageCharge !== undefined
      ? Number(variantPackageCharge) || 0
      : Number(product?.packageCharge) || 0;
  return {
    price,
    offerPrice,
    effectivePrice: offerPrice > 0 ? offerPrice : price,
    packageCharge,
  };
};

/**
 * Turn product search results into pickable options: a product with more
 * than one variant becomes one option per variant (e.g. "Chicken Mandi
 * (Half)"), each carrying only that variant so resolveProductPricing prices
 * it from that variant. Single/no-variant products pass through unchanged.
 *
 * A variant option's `_id` is a composite line key; `productId` stays the
 * real product id and `variantId` / `variantUnit` identify the variant.
 */
export const expandVariantOptions = (products = []) =>
  products.flatMap((p) => {
    if (!Array.isArray(p?.variants) || p.variants.length <= 1) return [p];
    return p.variants.map((v) => ({
      ...p,
      _id: `${p._id}__${v._id}`,
      productId: p._id,
      variantId: v._id,
      variantUnit: v.unit,
      productName: `${p.productName} (${v.unit})`,
      variants: [v],
    }));
  });
